// Non-billable ICD-10-CM parent ("category") codes: a code is a parent exactly
// when another code strictly extends it (F31 -> F31.9, Z83.4 -> Z83.49). The
// set is generated by scripts/generateIcd10Parents.js and code-split out of the
// main bundle, so it arrives asynchronously; every consumer must treat "not
// loaded yet" (null) as "no parent information" rather than guessing.

import { normalizeIcd10Code } from "../icd10Library";

let loadPromise = null;
let loadedSet = null;

export function loadIcd10ParentCodes() {
  if (!loadPromise) {
    loadPromise = import("./icd10ParentCodes")
      .then((mod) => {
        const raw = typeof mod?.default === "string" ? mod.default : "";
        const set = new Set(raw ? raw.split(",") : []);
        if (set.size === 0) throw new Error("ICD-10 parent code list is empty");
        loadedSet = set;
        return set;
      })
      .catch((e) => {
        console.error("Failed to load ICD-10 parent codes", e);
        // Evict the failed promise so the next caller retries the import: a
        // transient chunk failure (flaky network, stale hash mid-deploy) must
        // not disable chips and badges for the rest of the session. Same rule
        // as cachedFetch in ../icd10Search.js.
        loadPromise = null;
        return null;
      });
  }
  return loadPromise;
}

export function getLoadedIcd10ParentCodes() {
  return loadedSet;
}

export function isIcd10ParentCode(code, set) {
  if (!code || !set || typeof set.has !== "function" || set.size === 0) return false;
  return set.has(normalizeIcd10Code(code));
}

// Edit Codes picker (ICD-10 only): tag parents as non-billable and move them
// below every billable match. Stable partition, so the incoming ranking (and
// org custom-library priority within it) is preserved inside each group. With
// no set loaded the input is returned untouched.
export function flagNonBillableIcd10Results(items, set) {
  if (!Array.isArray(items)) return [];
  if (!set || set.size === 0) return items;
  const billable = [];
  const parents = [];
  for (const item of items) {
    if (item && isIcd10ParentCode(item.n, set)) parents.push({ ...item, nonBillable: true });
    else billable.push(item);
  }
  return parents.length === 0 ? items : billable.concat(parents);
}

// Edit Codes picker "hide" mode: drop parent rows from the results entirely.
// Same contract as flagNonBillableIcd10Results: the input reference comes back
// unchanged when the set is missing or nothing is a parent, so callers can
// cheaply detect "nothing to do".
export function hideNonBillableIcd10Results(items, set) {
  if (!Array.isArray(items)) return [];
  if (!set || set.size === 0) return items;
  const kept = items.filter((item) => !(item && isIcd10ParentCode(item.n, set)));
  return kept.length === items.length ? items : kept;
}

export function __resetIcd10ParentCodesForTests() {
  loadPromise = null;
  loadedSet = null;
}
